import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { TrendingUp, Shield, Users, Target, ArrowRight, Search, FileCheck, Handshake, Rocket } from "lucide-react";
import SEO from "@/components/SEO";
import { useCountUp } from "@/hooks/useCountUp";
import { AnimatedSection } from "@/components/AnimatedSection";

function StatCounter({ end, decimals = 0, prefix = '', suffix = '' }: { end: number; decimals?: number; prefix?: string; suffix?: string }) {
  const { displayValue, elementRef } = useCountUp({ end, decimals, prefix, suffix, duration: 2500 });
  return (
    <div ref={elementRef} className="text-4xl font-bold text-red-accent mb-2">
      {displayValue}
    </div>
  );
}

export default function Home() {
  const approachSteps = [
    {
      icon: Search,
      title: "Identify & Evaluate",
      description: "We conduct rigorous market analysis to identify well-established trucking and logistics companies with strong operational foundations and growth potential."
    },
    {
      icon: FileCheck,
      title: "Due Diligence",
      description: "Our comprehensive evaluation process examines financial performance, operational metrics, fleet condition, customer relationships, and management capabilities."
    },
    {
      icon: Handshake,
      title: "Partnership Approach",
      description: "We structure deals that align interests, respect legacy, and provide founders with liquidity while maintaining operational continuity and team stability."
    },
    {
      icon: Rocket,
      title: "Value Creation",
      description: "Post-acquisition, we implement strategic initiatives focused on operational excellence, technology adoption, talent development, and sustainable growth."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO />
      {/* Hero Section - Diagonal Split Design */}
      <section className="relative text-primary-foreground overflow-hidden min-h-[90vh] flex items-center">
        {/* Video Background with Diagonal Clip */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 w-full h-full"
            style={{
              clipPath: 'polygon(0 0, 100% 0, 100% 92%, 0 100%)',
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover scale-105"
            >
              <source src="/hero-video.mp4" type="video/mp4" />
              <track kind="captions" srcLang="en" label="English" />
            </video>
            {/* Gradient Overlay on Video */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/80 to-primary/70"></div>
          </div>
          
          {/* Solid Background Below Diagonal */}
          <div 
            className="absolute inset-0 bg-primary"
            style={{
              clipPath: 'polygon(0 92%, 100% 88%, 100% 100%, 0 100%)',
            }}
          ></div>
        </div>
        
        <div className="container relative pt-12 pb-20 lg:pt-16 lg:pb-32 z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="max-w-2xl">
              <div className="inline-block mb-4 px-4 py-2 bg-red-accent/20 backdrop-blur-sm rounded-full border border-red-accent/30 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <span className="text-sm font-semibold text-red-accent-foreground">Sponsor-Driven Platform</span>
              </div>
              
              <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl mb-6 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-100">
                Acquiring Excellence in{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-accent to-white animate-gradient bg-[length:200%_auto]">
                  Trucking & Logistics
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-primary-foreground/95 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-200 leading-relaxed">
                We identify, acquire, and grow exceptional companies through disciplined governance and strategic direction.
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-6 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-accent mb-1">$940B</div>
                  <div className="text-xs text-primary-foreground/80">Industry Size</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-accent mb-1">71%</div>
                  <div className="text-xs text-primary-foreground/80">Freight by Truck</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl font-bold text-accent mb-1">3.5M</div>
                  <div className="text-xs text-primary-foreground/80">Truck Drivers</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-400">
                <Link href="/about">
                  <Button 
                    size="lg" 
                    variant="secondary" 
                    className="text-base group shadow-lg hover:shadow-xl transition-all"
                    aria-label="Learn more about Stanford Logistics Management"
                  >
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link href="/contact">
                  <Button size="lg" className="text-base bg-red-accent hover:bg-red-accent/90 text-red-accent-foreground border-0 shadow-lg hover:shadow-xl transition-all">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right Content - Feature Cards */}
            <div className="hidden lg:grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-right-4 duration-1000 delay-500">
              <div className="bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-xl p-6 hover:bg-primary-foreground/10 hover:border-accent/30 transition-all group h-full">
                <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center mb-4 group-hover:bg-accent/30 transition-colors">
                  <Shield className="h-6 w-6 text-accent" />
                </div>
                <div className="font-semibold mb-2 text-white text-base">Disciplined Governance</div>
                <p className="text-sm text-primary-foreground/80 leading-relaxed">Strategic oversight that strengthens operations</p>
              </div>
              
              <div className="bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-xl p-6 hover:bg-primary-foreground/10 hover:border-accent/30 transition-all group h-full">
                <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center mb-4 group-hover:bg-accent/30 transition-colors">
                  <TrendingUp className="h-6 w-6 text-accent" />
                </div>
                <div className="font-semibold mb-2 text-white text-base">Long-Term Value</div>
                <p className="text-sm text-primary-foreground/80 leading-relaxed">Sustainable growth, not short-term gains</p>
              </div>
              
              <div className="bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-xl p-6 hover:bg-primary-foreground/10 hover:border-accent/30 transition-all group h-full">
                <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center mb-4 group-hover:bg-accent/30 transition-colors">
                  <Users className="h-6 w-6 text-accent" />
                </div>
                <div className="font-semibold mb-2 text-white text-base">Leadership Development</div>
                <p className="text-sm text-primary-foreground/80 leading-relaxed">Investing in people and teams</p>
              </div>
              
              <div className="bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-xl p-6 hover:bg-primary-foreground/10 hover:border-accent/30 transition-all group h-full">
                <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center mb-4 group-hover:bg-accent/30 transition-colors">
                  <Target className="h-6 w-6 text-accent" />
                </div>
                <div className="font-semibold mb-2 text-white text-base">Strategic Direction</div>
                <p className="text-sm text-primary-foreground/80 leading-relaxed">Clear vision and execution</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <AnimatedSection className="relative py-16 lg:py-24 overflow-hidden -mt-8">
        {/* Diagonal Background Extension from Hero */}
        <div className="absolute inset-0 bg-primary" 
          style={{
            clipPath: 'polygon(0 0, 100% 4%, 100% 100%, 0 100%)',
          }}
        ></div>
        
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6 text-white">
              Our Mission
            </h2>
            <p className="text-lg text-white/90 leading-relaxed">
              To acquire high-quality trucking and logistics companies and provide strategic oversight that enhances operations, strengthens leadership teams, and unlocks long-term value. We partner with exceptional operators to build enduring enterprises that set industry standards.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Core Values */}
      <AnimatedSection className="py-16 lg:py-24 bg-muted/30" delay={100}>
        <div className="container">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-foreground">
              Our Approach
            </h2>
            <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
              We bring a disciplined, strategic approach to value creation in the logistics sector
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {approachSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className="bg-card rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-border"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-red-accent/10 flex items-center justify-center text-red-accent">
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-3 text-card-foreground">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* Investment Criteria */}
      <AnimatedSection className="py-16 lg:py-24 bg-muted/30" delay={150}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-foreground">
              Investment Criteria
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We seek to acquire well-established trucking and logistics companies that demonstrate operational excellence and growth potential.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-xl border border-border">
              <h3 className="text-xl font-semibold mb-3 text-card-foreground">Company Profile</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Established operations with proven track record</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Strong customer relationships and contracts</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Experienced management teams</span>
                </li>
              </ul>
            </div>
            <div className="bg-card p-6 rounded-xl border border-border">
              <h3 className="text-xl font-semibold mb-3 text-card-foreground">Geographic Focus</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>North American markets</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Strategic regional coverage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Expansion opportunities in key corridors</span>
                </li>
              </ul>
            </div>
            <div className="bg-card p-6 rounded-xl border border-border">
              <h3 className="text-xl font-semibold mb-3 text-card-foreground">Operational Excellence</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Strong safety records and compliance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Modern fleet and equipment</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-accent mr-2">•</span>
                  <span>Scalable operations and systems</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Why Partner with Stanford */}
      <AnimatedSection className="py-16 lg:py-24 bg-background" delay={100}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6 text-foreground">
                Why Partner with Stanford Logistics Management
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                We understand that selling your business is one of the most important decisions you'll make. Stanford Logistics Management offers a partnership approach that preserves your company's legacy while unlocking new growth opportunities.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-accent/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-red-accent"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-card-foreground mb-1">Preserve Your Legacy</h3>
                    <p className="text-muted-foreground">We respect the businesses you've built and work to maintain company culture and employee relationships.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-accent/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-red-accent"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-card-foreground mb-1">Strategic Resources</h4>
                    <p className="text-muted-foreground">Access to capital, industry expertise, and operational best practices to accelerate growth.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-accent/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-red-accent"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-card-foreground mb-1">Long-Term Partnership</h4>
                    <p className="text-muted-foreground">We're not financial engineers—we're operators focused on building enduring value over decades.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/trucks-fleet.jpg"
                alt="Professional trucking fleet"
                className="rounded-xl shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Industry Statistics */}
      <AnimatedSection className="py-16 lg:py-24 bg-muted/30" delay={150}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-foreground">
              The Opportunity in Trucking & Logistics
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The trucking and logistics sector represents a massive, essential industry with significant consolidation opportunities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <StatCounter end={940} decimals={0} prefix="$" suffix="B" />
              <div className="text-sm text-muted-foreground">U.S. Trucking Industry Revenue</div>
            </div>
            <div className="text-center">
              <StatCounter end={71} decimals={0} suffix="%" />
              <div className="text-sm text-muted-foreground">Of U.S. Freight by Weight</div>
            </div>
            <div className="text-center">
              <StatCounter end={3.5} decimals={1} suffix="M" />
              <div className="text-sm text-muted-foreground">Commercial Truck Drivers</div>
            </div>
            <div className="text-center">
              <StatCounter end={2.26} decimals={2} prefix="$" />
              <div className="text-sm text-muted-foreground">Average Cost Per Mile (2024)</div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Industry Insights CTA */}
      <AnimatedSection className="py-16 lg:py-24 bg-background" delay={150}>
        <div className="container">
          <div className="bg-gradient-to-r from-primary to-primary/90 rounded-2xl p-8 lg:p-12 text-primary-foreground">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
                Industry Insights
              </h2>
              <p className="text-lg text-primary-foreground/90 mb-6 leading-relaxed">
                Stay informed about the latest trends, data, and analysis shaping the trucking and logistics industry. Our research-backed insights help you understand the forces driving M&A activity and operational excellence.
              </p>
              <Link href="/blog">
                <Button size="lg" className="text-base group bg-red-accent hover:bg-red-accent/90 text-red-accent-foreground">
                  Read Our Insights
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Contact & Location */}
      <AnimatedSection className="py-16 lg:py-24 bg-muted/30" delay={100}>
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
                Get in Touch
              </h2>
              <p className="text-lg text-muted-foreground">
                Ready to discuss your business? Contact our team to learn more about partnership opportunities.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-6">
                {/* Phone */}
                <div className="bg-card rounded-xl p-6 border border-border flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-red-accent/10 flex items-center justify-center text-red-accent flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-foreground">Phone</h3>
                    <a href="tel:+12014703182" className="text-red-accent hover:underline font-medium text-lg">
                      201-470-3182
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-card rounded-xl p-6 border border-border flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-red-accent/10 flex items-center justify-center text-red-accent flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-foreground">Email</h3>
                    <a href="mailto:info@stanfordlogistics.net" className="text-red-accent hover:underline font-medium break-all">
                      info@stanfordlogistics.net
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-card rounded-xl p-6 border border-border flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-red-accent/10 flex items-center justify-center text-red-accent flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1 text-foreground">Location</h3>
                    <p className="text-muted-foreground font-medium text-lg">
                      Savannah, GA
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-card rounded-xl border border-border overflow-hidden h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d107134.35946435384!2d-81.17723383281249!3d32.08089795!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88fb9e1d6f1e4b4d%3A0x4e0f0c5c8c8e8e8e!2sSavannah%2C%20GA!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Savannah, GA Location"
                />
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
}
